import { useCallback, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { Sidenav } from "../../components";
import { CustomersTable } from "./CustomersTable";
import { CustomersFilter } from "./CustomersFilter";
import { CustomersListColumnCustomisation } from "./CustomersListColumnCustomisation";
import { useAuth } from "../../hooks";
import { useAnalytics } from "../../hooks/useAnalytics";
import { customerFilters } from "../../utils";

const PAGE_SIZE = 15;

export const CustomerListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(() => {
    // Restore filters from URL query params
    const restoredFilters = [];
    Object.entries(customerFilters).forEach(([filterKey, filter]) => {
      if (searchParams.get(filter.queryKey) === filter.queryVal) {
        restoredFilters.push(filterKey);
      }
    });
    return restoredFilters;
  });
  const [columns, setColumns] = useState([]);
  const { apiClient } = useAuth();
  const [accounts, setAccounts] = useState([]);

  // Initialize searchTerm from URL parameters
  const [searchTerm, setSearchTerm] = useState(() => {
    return searchParams.get("search") || "";
  });

  // Initialize pagination from URL parameters
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get("page") || "1", 10),
    pageSize: 0,
    total: 0,
    next: null,
    totalPages: 0,
  });
  // Initialize ordering from URL parameters
  const [ordering, setOrdering] = useState(() => {
    return searchParams.get("ordering") || "";
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const isInitialMount = useRef(true);
  const prevFiltersRef = useRef(selectedFilters);

  const customerList = accounts;

  // Helper function to update URL with new parameters
  const updateURLParams = useCallback(
    (newParams) => {
      const currentParams = new URLSearchParams(searchParams);

      // Update with new parameters
      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          currentParams.set(key, value);
        } else {
          currentParams.delete(key);
        }
      });

      setSearchParams(currentParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const fetchAccountsCallback = useCallback(
    async (
      page = 1,
      search = "",
      filters = [],
      isSearchRequest = false,
      sortOrder = "",
    ) => {
      // Prevent duplicate requests
      if (isSearchRequest && isSearching) {
        return;
      }

      // Use different loading states for initial load vs search
      if (isSearchRequest) {
        setIsSearching(true);
      } else {
        setIsLoading(true);
      }

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: PAGE_SIZE.toString(),
      });

      // Add search parameter if provided
      if (search.trim()) {
        params.append("search", search.trim());
      }

      // Add ordering parameter if provided
      if (sortOrder) {
        params.append("ordering", sortOrder);
      }

      // Add filter parameters
      filters.forEach((filterKey) => {
        const filter = customerFilters[filterKey];
        if (filter) {
          params.append(filter.queryKey, filter.queryVal);
        }
      });

      try {
        const response = await apiClient.get(`accounts/?${params.toString()}`);
        const results = response.data.results ?? [];
        setAccounts(results);

        const totalPages = Math.ceil(response.data.count / PAGE_SIZE);

        setPagination({
          page: page,
          pageSize: Math.min(PAGE_SIZE, response.data.count),
          total: response.data.count,
          next: response.data.next,
          totalPages: totalPages,
        });
      } catch (error) {
        console.error("Error fetching accounts:", error);
        if (error.response?.status !== 401) {
          console.error("Failed to fetch customers");
        }
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiClient, isSearching],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      // Load with search term, page, and filters from URL on initial mount
      const urlSearchTerm = searchParams.get("search") || "";
      const urlPage = parseInt(searchParams.get("page") || "1", 10);
      const urlOrdering = searchParams.get("ordering") || "";
      fetchAccountsCallback(
        urlPage,
        urlSearchTerm,
        selectedFilters,
        false,
        urlOrdering,
      );
      isInitialMount.current = false;
    }
  }, [fetchAccountsCallback, searchParams, selectedFilters]);

  // Handle filter changes separately - whenever selectedFilters actually change
  useEffect(() => {
    if (!isInitialMount.current) {
      // Check if filters actually changed
      const filtersChanged =
        JSON.stringify(prevFiltersRef.current) !==
        JSON.stringify(selectedFilters);

      if (filtersChanged) {
        prevFiltersRef.current = selectedFilters;

        // Reset to page 1 when filters change
        const newPage = 1;
        setPagination((prev) => ({ ...prev, page: newPage }));

        // Fetch data with new filters (URL is already updated by handleFilterToggle)
        fetchAccountsCallback(
          newPage,
          searchTerm,
          selectedFilters,
          false,
          ordering,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  // Handle owner scope changes - refetch data when ownerScope changes
  useEffect(() => {
    const handleOwnerScopeChange = () => {
      if (!isInitialMount.current) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        fetchAccountsCallback(1, searchTerm, selectedFilters, false, ordering);
      }
    };

    window.addEventListener("ownerScopeChanged", handleOwnerScopeChange);
    return () => {
      window.removeEventListener("ownerScopeChanged", handleOwnerScopeChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAccountsCallback, searchTerm, selectedFilters]);

  const handleFilterToggle = (filterAdded, filter) => {
    const currentParams = new URLSearchParams(searchParams);

    trackEvent("customer_filter_changed", {
      filter: filter.filterKey,
      action: filterAdded ? "added" : "removed",
    });

    if (filterAdded) {
      currentParams.set(filter.queryKey, filter.queryVal);
      currentParams.set("page", "1");
      setSearchParams(currentParams, { replace: true });
    } else {
      currentParams.delete(filter.queryKey);
      currentParams.set("page", "1");
      setSearchParams(currentParams, { replace: true });
    }

    setSelectedFilters((prevSelected) => {
      if (filterAdded) {
        return [...prevSelected, filter.filterKey];
      } else {
        return prevSelected.filter((key) => key !== filter.filterKey);
      }
    });
  };

  const handleFiltersClose = () => setIsFiltersOpen(false);
  const handleColumnsClose = () => setIsColumnsOpen(false);

  const handleFiltersOpen = () => setIsFiltersOpen(true);
  const handleColumnsOpen = () => setIsColumnsOpen(true);

  const handleSearch = useCallback(
    (term) => {
      setSearchTerm(term);

      // Update URL with search term and reset page to 1
      const newParams = { page: "1" };
      if (term.trim()) {
        newParams.search = term;
      } else {
        newParams.search = ""; // Explicitly clear search param from URL
      }
      updateURLParams(newParams);

      const trimmedTerm = term.trim();

      if (
        trimmedTerm === "" ||
        trimmedTerm.length >= 3 ||
        trimmedTerm.includes(" ")
      ) {
        if (trimmedTerm) {
          trackEvent("customer_search", { query_length: trimmedTerm.length });
        }
        fetchAccountsCallback(1, term, selectedFilters, true, ordering);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchAccountsCallback, updateURLParams, selectedFilters, ordering],
  );

  // Handle page changes from pagination component
  const handlePageChange = useCallback(
    (newPage) => {
      // Update local state
      setPagination((prev) => ({ ...prev, page: newPage }));

      // Update URL
      updateURLParams({ page: newPage.toString() });

      // Fetch data for the new page
      fetchAccountsCallback(
        newPage,
        searchTerm,
        selectedFilters,
        false,
        ordering,
      );
    },
    [
      fetchAccountsCallback,
      searchTerm,
      selectedFilters,
      ordering,
      updateURLParams,
    ],
  );

  // Handle column header click for sorting
  const handleOrderingChange = useCallback(
    (newOrdering) => {
      setOrdering(newOrdering);

      // Update URL
      updateURLParams({
        ordering: newOrdering || null,
        page: "1",
      });

      // Reset to page 1 and fetch
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchAccountsCallback(1, searchTerm, selectedFilters, false, newOrdering);
    },
    [fetchAccountsCallback, searchTerm, selectedFilters, updateURLParams],
  );

  return (
    <div className="flex w-full flex-row">
      <Sidenav />
      <CustomersTable
        customerList={customerList}
        handleFiltersOpen={handleFiltersOpen}
        handleColumnsOpen={handleColumnsOpen}
        pagination={pagination}
        fetchNextPage={handlePageChange}
        isLoading={isLoading}
        isSearching={isSearching}
        selectedFilters={selectedFilters}
        handleFilterToggle={handleFilterToggle}
        columns={columns}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        ordering={ordering}
        onOrderingChange={handleOrderingChange}
      />

      <CustomersFilter
        isOpen={isFiltersOpen}
        handleClose={handleFiltersClose}
        selectedFilters={selectedFilters}
        handleFilterToggle={handleFilterToggle}
      />
      <CustomersListColumnCustomisation
        isOpen={isColumnsOpen}
        handleClose={handleColumnsClose}
        columns={columns}
        setColumns={setColumns}
      />
    </div>
  );
};
